/**
 * @file EmailVerificationPending.jsx
 * @description Full-screen gate shown after signup when email verification is
 *   required. Lets the user resend the verification email with a 60-second cooldown.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/EmailVerificationPending
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { verificationResendOutcome } from "../lib/verificationResend";
import { T } from "../theme";

export default function EmailVerificationPending({ email, onSignOut }) {
  const { t } = useTranslation();
  const [resending, setResending]       = useState(false);
  const [cooldown,  setCooldown]        = useState(0);   // seconds remaining
  const [lastMsg,   setLastMsg]         = useState("");

  // Tick the cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(id); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  async function handleResend() {
    if (resending || cooldown > 0) return;
    setResending(true);
    setLastMsg("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");

      const res = await supabase.functions.invoke("send-verification-email", {
        body: {},
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      // The cooldown answer is a 429, which supabase-js hands over as an error with data null: the outcome reads the
      // status and body from the error.
      const outcome = await verificationResendOutcome(res);

      if (outcome.kind === "cooldown") {
        const wait = outcome.waitSeconds ?? 60;
        setCooldown(wait);
        setLastMsg(t('emailVerification.errorRateLimited', { seconds: wait }));
      } else if (outcome.kind === "sent") {
        setCooldown(60);
        setLastMsg(t('emailVerification.successMessage'));
      } else {
        setLastMsg(t('emailVerification.errorGeneric'));
      }
    } catch (err) {
      setLastMsg(err.message || t('emailVerification.errorGeneric'));
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    }}>
      <div style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        padding: "40px 36px",
        maxWidth: 480,
        width: "100%",
        textAlign: "center",
        boxShadow: T.shadowMd,
      }}>
        {/* Icon */}
        <div style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: T.blueLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: 30,
        }}>
          ✉️
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: "0 0 10px" }}>
          {t('emailVerification.heading')}
        </h2>

        <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, margin: "0 0 6px" }}>
          {t('emailVerification.sentLink')}
        </p>
        <p style={{
          fontSize: 14,
          fontWeight: 600,
          color: T.blue,
          background: T.blueLight,
          borderRadius: 6,
          padding: "6px 14px",
          display: "inline-block",
          margin: "0 0 20px",
          wordBreak: "break-all",
        }}>
          {email}
        </p>

        <p style={{ fontSize: 13, color: T.textLight, lineHeight: 1.6, margin: "0 0 24px" }}>
          {t('emailVerification.instructions')}
        </p>

        {/* Resend button */}
        <button
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          style={{
            display: "block",
            width: "100%",
            padding: "11px 0",
            background: resending || cooldown > 0 ? T.border : T.blue,
            color: resending || cooldown > 0 ? T.textLight : "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: resending || cooldown > 0 ? "not-allowed" : "pointer",
            transition: "background 0.15s",
            marginBottom: 12,
          }}
        >
          {resending
            ? t('emailVerification.buttonResendLoading')
            : cooldown > 0
              ? t('emailVerification.buttonResendCooldown', { seconds: cooldown })
              : t('emailVerification.buttonResend')}
        </button>

        {lastMsg && (
          <p style={{
            fontSize: 13,
            color: lastMsg.includes("sent") ? T.green : T.red,
            margin: "0 0 16px",
          }}>
            {lastMsg}
          </p>
        )}

        {/* Sign out */}
        <button
          onClick={onSignOut}
          style={{
            background: "none",
            border: "none",
            color: T.textLight,
            fontSize: 13,
            cursor: "pointer",
            textDecoration: "underline",
            padding: 0,
          }}
        >
          {t('emailVerification.wrongEmail')}
        </button>

        <p style={{ fontSize: 12, color: T.textXLight, marginTop: 20 }}>
          {t('emailVerification.spamNote')}
        </p>
      </div>
    </div>
  );
}
